import { useEffect, useRef, useState } from "react"
import { resolveLaunchLink, validateLaunchLink, type ShortlistLaunchLink } from "@shared/ShortlistLaunchLink"

export function ShortlistLaunchLinkSettings({
	slug,
	request,
}: {
	slug: string
	request: (url: string, method?: string, body?: unknown) => Promise<any>
}) {
	const [mode, setMode] = useState<"full" | "builder">("full")
	const [fullLink, setFullLink] = useState("")
	const [baseUrl, setBaseUrl] = useState("https://chatgpt.com/")
	const [parameters, setParameters] = useState<Array<{ name: string; value: string }>>([])
	const [loaded, setLoaded] = useState(false)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState("")
	const [message, setMessage] = useState("")
	const [reload, setReload] = useState(0)
	const active = useRef(true)
	const requestRef = useRef(request)
	requestRef.current = request
	const endpoint = `${import.meta.env.VITE_BACKEND_URL || ""}/api/v1/search/shortlist/stores/${encodeURIComponent(slug)}/launch-link`
	function apply(link: ShortlistLaunchLink | null) {
		setMode(link?.mode || "full")
		setFullLink(link?.mode === "full" ? link.url : "")
		setBaseUrl(link?.mode === "builder" ? link.baseUrl : "https://chatgpt.com/")
		setParameters(link?.mode === "builder" ? link.queryParameters : [])
	}
	useEffect(() => {
		let stopped = false
		active.current = true
		setLoaded(false)
		setError("")
		void requestRef
			.current(endpoint)
			.then((result) => {
				if (stopped) return
				apply(result.link)
				setLoaded(true)
			})
			.catch((e) => {
				if (!stopped) setError(e.message)
			})
		return () => {
			stopped = true
			active.current = false
		}
	}, [endpoint, reload])
	const draft: ShortlistLaunchLink = mode === "full" ? { mode, url: fullLink } : { mode, baseUrl, queryParameters: parameters }
	let preview = ""
	try {
		preview = resolveLaunchLink(validateLaunchLink(draft))
	} catch {
		/* Incomplete drafts have no preview. */
	}
	async function save(clear = false) {
		setError("")
		setMessage("")
		try {
			const link = clear ? null : validateLaunchLink(draft)
			setBusy(true)
			const result = await requestRef.current(endpoint, "PUT", { link })
			if (!active.current) return
			apply(result.link)
			setMessage(
				clear
					? "Custom link cleared. The page will use its default link."
					: "Button link saved. Changes apply when the brand page is opened or refreshed.",
			)
		} catch (e: any) {
			if (active.current) setError(e.message)
		} finally {
			if (active.current) setBusy(false)
		}
	}
	return (
		<section className="mt-6 border-t border-border pt-4">
			<h3 className="font-semibold">ChatGPT button link</h3>
			<p className="text-sm text-muted-foreground mt-1 mb-3">
				Choose how to set this brand’s button destination. HTTPS links only. Parameter values are encoded automatically;
				enter plain text.
			</p>
			{!loaded && !error && <p role="status">Loading link settings…</p>}
			{error && (
				<p className="text-sm text-red-600" role="alert">
					{error}
				</p>
			)}
			{!loaded && error && (
				<button type="button" className="border rounded px-3 py-2 mt-2" onClick={() => setReload((n) => n + 1)}>
					Retry loading link
				</button>
			)}
			<fieldset disabled={!loaded || busy} className="space-y-3 mt-3">
				<legend className="sr-only">Button link format</legend>
				<div className="flex flex-wrap gap-4">
					<label className="flex gap-2 items-center">
						<input
							type="radio"
							name={`launch-link-mode-${slug}`}
							checked={mode === "full"}
							onChange={() => {
								setMode("full")
								setMessage("")
							}}
						/>
						Full link
					</label>
					<label className="flex gap-2 items-center">
						<input
							type="radio"
							name={`launch-link-mode-${slug}`}
							checked={mode === "builder"}
							onChange={() => {
								setMode("builder")
								setMessage("")
							}}
						/>
						Base URL + query parameters
					</label>
				</div>
				{mode === "full" ? (
					<label className="block text-sm">
						Full link
						<textarea
							className="block w-full border rounded p-2 mt-1"
							rows={4}
							maxLength={32768}
							value={fullLink}
							onChange={(e) => {
								setFullLink(e.target.value)
								setMessage("")
							}}
							placeholder="https://chatgpt.com/?q=…"
						/>
					</label>
				) : (
					<>
						<label className="block text-sm">
							Base URL
							<input
								type="url"
								className="block w-full border rounded p-2 mt-1"
								maxLength={32768}
								value={baseUrl}
								onChange={(e) => {
									setBaseUrl(e.target.value)
									setMessage("")
								}}
							/>
						</label>
						<p className="text-sm text-muted-foreground">
							Existing query parameters and fragments in the base URL are preserved. Repeated parameter names are
							allowed.
						</p>
						{parameters.map((row, index) => (
							<div key={index} className="flex flex-wrap items-end gap-2">
								<label className="text-sm">
									Parameter name
									<input
										className="block border rounded p-2 mt-1"
										aria-label={`Parameter ${index + 1} name`}
										value={row.name}
										maxLength={200}
										onChange={(e) => {
											setParameters((rows) =>
												rows.map((r, i) => (i === index ? { ...r, name: e.target.value } : r)),
											)
											setMessage("")
										}}
									/>
								</label>
								<label className="text-sm flex-1 min-w-48">
									Parameter value
									<textarea
										className="block w-full border rounded p-2 mt-1"
										aria-label={`Parameter ${index + 1} value`}
										value={row.value}
										maxLength={16000}
										rows={2}
										onChange={(e) => {
											setParameters((rows) =>
												rows.map((r, i) => (i === index ? { ...r, value: e.target.value } : r)),
											)
											setMessage("")
										}}
									/>
								</label>
								<button
									type="button"
									className="border rounded px-3 py-2"
									aria-label={`Remove parameter ${index + 1}`}
									onClick={() => {
										setParameters((rows) => rows.filter((_, i) => i !== index))
										setMessage("")
									}}>
									Remove
								</button>
							</div>
						))}
						<button
							type="button"
							className="border rounded px-3 py-2"
							disabled={parameters.length >= 50}
							onClick={() => {
								setParameters((rows) => [...rows, { name: "", value: "" }])
								setMessage("")
							}}>
							Add query parameter
						</button>
					</>
				)}
				{preview && (
					<div className="text-sm">
						<p className="font-medium">Resulting link</p>
						<p className="break-all mt-1">{preview}</p>
					</div>
				)}
				<div className="flex gap-2 flex-wrap">
					<button type="button" className="border rounded px-3 py-2 disabled:opacity-50" onClick={() => void save()}>
						{busy ? "Saving…" : "Save button link"}
					</button>
					<button
						type="button"
						className="border rounded px-3 py-2 disabled:opacity-50"
						onClick={() => void save(true)}>
						Use default link
					</button>
				</div>
			</fieldset>
			{message && (
				<p className="text-sm mt-3" role="status">
					{message}
				</p>
			)}
		</section>
	)
}
